import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Receipt as OrderIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as RevenueIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  ShoppingCart as CartIcon
} from '@mui/icons-material';
import { useBrand } from '../../context/BrandContext';
import { getRetailOrders } from '../../services/retailOrdersService';
import { StatusBadge } from '../ds';

function RetailDashboard() {
  const navigate = useNavigate();
  const { selectedBrand, brands, selectBrand } = useBrand();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dashboard data
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingPayment: 0,
    pendingFulfillment: 0,
    totalRevenue: 0,
    averageOrderValue: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    if (selectedBrand?.id) {
      loadDashboardData();
    } else {
      setLoading(false);
    }
  }, [selectedBrand?.id]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      const ordersRes = await getRetailOrders(selectedBrand.id, { limit: 100 });
      // Handle response structure - service returns { success, data: ordersArray, statistics }
      const ordersData = ordersRes?.data;
      const orders = Array.isArray(ordersData) ? ordersData : [];
      const statistics = ordersRes?.statistics || {
        total_orders: 0,
        pending_payment: 0,
        pending_fulfillment: 0,
        total_revenue: 0
      };

      // Use statistics from API (counts all records, not just paginated)
      setStats({
        totalOrders: statistics.total_orders,
        pendingPayment: statistics.pending_payment,
        pendingFulfillment: statistics.pending_fulfillment,
        totalRevenue: statistics.total_revenue,
        averageOrderValue: statistics.total_orders > 0 ? statistics.total_revenue / statistics.total_orders : 0
      });

      // Sort by date and get recent orders (for display table only)
      const sorted = [...orders].sort((a, b) =>
        new Date(b.event_date || b.created_at) - new Date(a.event_date || a.created_at)
      );
      setRecentOrders(sorted.slice(0, 10));
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load retail dashboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBrandChange = (event) => {
    const brandId = event.target.value;
    const brand = brands.find(b => b.id === brandId);
    if (brand) {
      selectBrand(brand);
    }
  };

  const formatCurrency = (value, currency = 'GBP') => {
    const symbols = { GBP: '£', USD: '$', EUR: '€', AUD: 'A$' };
    return `${symbols[currency] || currency}${parseFloat(value || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      paid: 'success',
      delivered: 'success',
      partial: 'warning',
      shipped: 'warning',
      pending: 'info',
      processing: 'info',
      failed: 'error',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Retail
          </Typography>
          <Typography variant="body2" color="textSecondary">
            B2B wholesale and manual retail orders
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Brand</InputLabel>
            <Select
              value={selectedBrand?.id || ''}
              onChange={handleBrandChange}
              label="Brand"
              size="small"
            >
              {brands.map(brand => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/retail/orders')}
          >
            New Order
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={loadDashboardData} disabled={!selectedBrand?.id}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {!selectedBrand && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Please select a brand to view retail data
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {selectedBrand && (
        <>
          {/* Quick Stats */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}
                onClick={() => navigate('/retail/orders')}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">Total Orders</Typography>
                      <Typography variant="h4" fontWeight="bold">{stats.totalOrders}</Typography>
                    </Box>
                    <Box sx={{
                      backgroundColor: 'primary.light',
                      borderRadius: '50%',
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <OrderIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}
                onClick={() => navigate('/retail/orders')}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">Pending Payment</Typography>
                      <Typography variant="h4" fontWeight="bold">{stats.pendingPayment}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        awaiting payment
                      </Typography>
                    </Box>
                    <Box sx={{
                      backgroundColor: 'warning.light',
                      borderRadius: '50%',
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <RevenueIcon sx={{ color: 'warning.main', fontSize: 28 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}
                onClick={() => navigate('/retail/orders')}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">Pending Fulfillment</Typography>
                      <Typography variant="h4" fontWeight="bold">{stats.pendingFulfillment}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        to be shipped
                      </Typography>
                    </Box>
                    <Box sx={{
                      backgroundColor: 'info.light',
                      borderRadius: '50%',
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <CartIcon sx={{ color: 'info.main', fontSize: 28 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}
                onClick={() => navigate('/retail/orders')}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">Total Revenue (USD)</Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {formatCurrency(stats.totalRevenue, 'USD')}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Avg: {formatCurrency(stats.averageOrderValue, 'USD')}
                      </Typography>
                    </Box>
                    <Box sx={{
                      backgroundColor: 'success.light',
                      borderRadius: '50%',
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <TrendingUpIcon sx={{ color: 'success.main', fontSize: 28 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Recent Orders */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="600">Recent Orders</Typography>
                <Button
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/retail/orders')}
                  size="small"
                >
                  View All
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Payment</TableCell>
                      <TableCell>Fulfillment</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentOrders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Box sx={{ py: 4 }}>
                            <OrderIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                            <Typography color="textSecondary">
                              No retail orders yet
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<AddIcon />}
                              onClick={() => navigate('/retail/orders')}
                              sx={{ mt: 2 }}
                            >
                              Create First Order
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      recentOrders.map((order) => (
                        <TableRow
                          key={order.order_id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate('/retail/orders')}
                        >
                          <TableCell>
                            <Typography fontWeight="medium">
                              {order.order_name || `#${order.order_id}`}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatDate(order.event_date)}</TableCell>
                          <TableCell>{order.customer_name || '—'}</TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="medium">
                              {formatCurrency(order.total_amount, order.currency)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={order.payment_status || 'pending'} />
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={order.fulfillment_status || 'pending'} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>Quick Actions</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/retail/orders')}
                    sx={{ py: 1.5, justifyContent: 'flex-start' }}
                  >
                    Create Order
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<OrderIcon />}
                    onClick={() => navigate('/retail/orders')}
                    sx={{ py: 1.5, justifyContent: 'flex-start' }}
                  >
                    View All Orders
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}

export default RetailDashboard;
